import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl, baseurImage } from "../../Baseurl";
import AddInsuranceCard from "./AddInsuranceCard";
import { usePDF } from "react-to-pdf";
import EventBus from "../../Context/EventBus";
import axiosInstance from "../../api/axiosInstance";
import logo from "../../assests/logo.png";

export default function PatientInsurance({ patientId }) {
  // State for modals and data
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [insuranceCards, setInsuranceCards] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCardId, setEditingCardId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [insurances, setInsurances] = useState([]);
  const [editForm, setEditForm] = useState({
    issue_date: "",
    expiry_date: "",
    co_insurance_percent: "",
    deductible_amount: "",
    policy_number: "",
    company_id: "",
    scanned_copy: null,
  });

  const [showNewClaimModal, setShowNewClaimModal] = useState(false);
  const [claimForm, setClaimForm] = useState({
    card_id: "",
    invoice_id: "",
    is_deductible_applied: false,
    status: "Pending"
  });
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [claimCalculations, setClaimCalculations] = useState({
    invoiceAmount: 0,
    deductibleAmount: 0,
    afterDeductible: 0,
    coInsurancePercent: 0,
    claimAmount: 0,
    patientAmount: 0
  });

  const [overrideCoInsPercent, setOverrideCoInsPercent] = useState("");

  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);

  const [currentClaimPage, setCurrentClaimPage] = useState(1);
  const claimsPerPage = 10;

  const [activeClaimCards, setActiveClaimCards] = useState([]);
  const [activeCardsLoading, setActiveCardsLoading] = useState(false);

  const [showViewClaimModal, setShowViewClaimModal] = useState(false);
  const [viewingClaim, setViewingClaim] = useState(null);

  const [showEditClaimModal, setShowEditClaimModal] = useState(false);
  const [editingClaim, setEditingClaim] = useState(null);
  const [editClaimForm, setEditClaimForm] = useState({ is_deductible_applied: false, co_insurance_percent: "", manual_override: false, manual_insurance_amount: "", manual_patient_amount: "" });
  const [editClaimCalc, setEditClaimCalc] = useState({ invoiceAmount: 0, deductibleAmount: 0, afterDeductible: 0, coInsurancePercent: 0, claimAmount: 0, patientAmount: 0 });

  const { toPDF, targetRef } = usePDF({ filename: 'insurance-claim.pdf' });
  const [printData, setPrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  const [termsCondition, setTermsCondition] = useState(null);

  const [hospitalLogo, setHospitalLogo] = useState(logo);

  //fetch the terms and condition
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await axiosInstance.get("getTermsCondition/1");
        if (response.data.success && response.data.data) {
          const termData = Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data;
          setTermsCondition(termData);
        }
      }
      catch (error) {
        console.error("Error fetching terms:", error);
      }
    }
  }, []);

  // Load hospital logo like PatientBilling
  useEffect(() => {
    const loadLogo = async () => {
      try {
        // 1. Try to load from cache first
        const cached = localStorage.getItem("businessSettingImage");
        if (cached && cached.startsWith("data:image/")) {
          setHospitalLogo(cached);
        }

        // 2. Load from business settings
        const settings = JSON.parse(localStorage.getItem("businessSetting"));
        const bgImage = settings?.base64Image;
        const img = settings?.image;
        const source = bgImage || img;

        if (source) {
          if (source.startsWith("data:image/")) {
            setHospitalLogo(source);
            localStorage.setItem("businessSettingImage", source);
          } else if (!source.startsWith("data:text/html")) {
            // It is a URL or filename
            let url = source;
            if (!source.startsWith("http")) {
              if (bgImage === source) {
                url = `${baseurImage}business_setting/${source}`;
              } else {
                url = `${baseurImage}${source}`;
              }
            }

            const response = await fetch(url);
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.startsWith("image/")) {
              const blob = await response.blob();
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64 = reader.result;
                setHospitalLogo(base64);
                localStorage.setItem("businessSettingImage", base64);
              };
              reader.readAsDataURL(blob);
            }
          }
        }
      } catch (error) {
        console.error("Error loading logo:", error);
      }
    };
    loadLogo();
  }, []);

  useEffect(() => {
    loadInsuranceCards();
    loadInsuranceCompanies();
    loadInvoices();
    loadClaims();
  }, [patientId]);

  useEffect(() => {
    const off = EventBus.on("invoice:created", (e) => {
      try {
        const evtPatientId = e?.detail?.patientId;
        if (!evtPatientId) { loadInvoices(); return; }
        if (Number(evtPatientId) === Number(patientId)) {
          loadInvoices();
        }
      } catch (_) {
        loadInvoices();
      }
    });
    return () => { off && off(); };
  }, [patientId]);


  const formatDateOnly = (value) => {
    if (!value) return "";
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-GB");
      }
      return String(value).toString().split("T")[0];
    } catch (e) {
      return String(value).toString().split("T")[0] || String(value);
    }
  };


  const toInputDate = (value) => {
    if (!value) return "";
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      return String(value).split("T")[0];
    } catch (e) {
      return String(value).split("T")[0];
    }
  };

  const openEditModal = (card) => {
    setEditingCardId(card.card_id || card.id);
    setEditForm({
      issue_date: toInputDate(card.issue_date),
      expiry_date: toInputDate(card.expiry_date),
      co_insurance_percent: card.co_insurance_percent ?? "",
      deductible_amount: card.deductible_amount ?? "",
      policy_number: card.policy_number ?? "",
      company_id: card.company_id ?? "",
      scanned_copy: null,
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setEditForm((prev) => ({ ...prev, [name]: files[0] || null }));
    } else {
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const submitEdit = async (e) => {
    e?.preventDefault?.();
    if (!editingCardId) return;
    try {
      setLoading(true);
      const url = `editInsuranceCard/${editingCardId}`;

      const formData = new FormData();
      formData.append('issue_date', editForm.issue_date);
      formData.append('expiry_date', editForm.expiry_date);
      formData.append('co_insurance_percent', editForm.co_insurance_percent);
      formData.append('deductible_amount', editForm.deductible_amount);
      formData.append('policy_number', editForm.policy_number);
      formData.append('company_id', editForm.company_id);

      if (editForm.scanned_copy) {
        formData.append('scanned_copy', editForm.scanned_copy);
      }

      const res = await axiosInstance.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data?.success) {
        Swal.fire('Success', 'Insurance card updated successfully', 'success');
        setShowEditModal(false);
        setEditingCardId(null);
        await loadInsuranceCards();
      } else {
        Swal.fire('Error', res.data?.message || 'Failed to update insurance card', 'error');
      }
    } catch (error) {
      console.error('Edit insurance card error:', error);
      Swal.fire('Error', 'Failed to update insurance card', 'error');
    } finally {
      setLoading(false);
    }
  };


  const loadInsuranceCards = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`getAllInsuranceCards`);
      if (response.data.success) {

        const patientCards = response.data.data.filter(card => card.patient_id == patientId);
        setInsuranceCards(patientCards);
      }
    } catch (error) {
      console.error("Error loading insurance cards:", error);
    } finally {
      setLoading(false);
    }
  };


  const loadInsuranceCompanies = async () => {
    try {
      const response = await axiosInstance.get(`getActiveInsuranceCompanies`);
      if (response.data.success) {
        setInsuranceCompanies(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading insurance companies:", error);
    }
  };


  const handleDeleteCard = async (cardId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        const response = await axiosInstance.delete(`deleteInsuranceCard/${cardId?.card_id}`);
        if (response.data.success) {
          Swal.fire('Deleted!', 'Insurance card has been deleted.', 'success');
          loadInsuranceCards();
        } else {
          Swal.fire('Error', 'Failed to delete insurance card', 'error');
        }
      }
    } catch (error) {
      console.error("Error deleting insurance card:", error);
      Swal.fire('Error', 'Failed to delete insurance card', 'error');
    }
  };

  const handleCardAdded = () => {
    loadInsuranceCards();
  };

  const updateClaimStatus = async (claimId, newStatus) => {
    try {
      const resp = await axiosInstance.put(`updateInsuranceClaimStatus/${claimId}`, { status: newStatus });
      const data = resp.data || {};
      if (data && (data.success || data.ok)) {
        // Update in main list
        setClaims(prev => prev.map(c =>
          String(c.claim_id || c.id) === String(claimId) ? { ...c, status: (newStatus || '').toLowerCase() } : c
        ));
        Swal.fire('Success', 'Status updated successfully', 'success');
      } else {
        Swal.fire('Error', data.message || 'Failed to update status', 'error');
      }
    } catch (e) {
      console.error('Update claim status error:', e);
      Swal.fire('Error', 'Server error while updating status', 'error');
    }
  };

  // Load invoices for claims
  const loadInvoices = async () => {
    try {
      const response = await axiosInstance.get(`getUnusedInvoicesByPatient/${patientId}`);
      const list = Array.isArray(response.data?.invoices)
        ? response.data.invoices
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

      const normalized = (list || []).map((inv) => {
        const id = inv.invoiceId ?? inv.invoice_id ?? inv.id ?? inv.invoiceid;
        const dateStr = inv.invoice_date ?? inv.updated_at ?? inv.updatedAt ?? inv.date;
        return {
          ...inv,
          id,
          invoice_no: inv.invoice_no ?? inv.invoiceNo ?? id,
          invoice_date: dateStr,
          total_amount: inv.total_amount ?? inv.totalAmount ?? inv.amount ?? 0,
          remaining_amount: inv.remaining_amount ?? inv.remaining ?? inv.remainingAmount ?? 0,
          status_text: (inv.invoice_status || inv.status || "").toString().toLowerCase(),
        };
      });

      const filtered = normalized.filter((inv) => {
        const remaining = Number(inv.remaining_amount);
        if (!isNaN(remaining) && remaining > 0) return true;
        return inv.status_text === "not paid" || inv.status_text === "unpaid" || inv.status_text === "partial paid" || inv.status_text === "partially paid";
      });

      setInvoices(filtered);
    } catch (error) {
      console.error("Error loading invoices:", error);
      setInvoices([]);
    }
  };


  const openNewClaimModal = () => {
    setShowNewClaimModal(true);
    setClaimForm({
      card_id: "",
      invoice_id: "",
      is_deductible_applied: false,
      status: "Pending"
    });
    setSelectedInvoice(null);
    setOverrideCoInsPercent("");
    setClaimCalculations({
      invoiceAmount: 0,
      deductibleAmount: 0,
      afterDeductible: 0,
      coInsurancePercent: 0,
      claimAmount: 0,
      patientAmount: 0
    });
    loadActiveCardsForClaims();
  };

  const loadActiveCardsForClaims = async () => {
    try {
      setActiveCardsLoading(true);
      const response = await axiosInstance.get(`getInsuranceCardsByPatientId/${patientId}`);
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setActiveClaimCards(list);
    } catch (error) {
      console.error("Error loading active insurance cards:", error);
      setActiveClaimCards([]);
    } finally {
      setActiveCardsLoading(false);
    }
  };

  const loadClaims = async () => {
    try {
      setClaimsLoading(true);

      const response = await axiosInstance.get(`getInsuranceClaimsByPatientId/${patientId}`);
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setClaims(list);
    } catch (error) {
      console.error("Error loading insurance claims:", error);
      setClaims([]);
    } finally {
      setClaimsLoading(false);
    }
  };

  const handleClaimFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClaimForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredClaims = claims.filter(
    (claim) =>
      claim.claim_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.invoice_total?.toString().includes(searchTerm) ||
      claim.insurance_amount?.toString().includes(searchTerm) ||
      claim.patient_amount?.toString().includes(searchTerm) ||
      claim.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalClaimPages = Math.ceil(filteredClaims.length / claimsPerPage) || 1;
  const currentClaims = filteredClaims.slice(
    (currentClaimPage - 1) * claimsPerPage,
    currentClaimPage * claimsPerPage
  );


  useEffect(() => {
    if (claimForm.card_id && claimForm.invoice_id) {

      const selectedCard = (activeClaimCards.find(card => (card.card_id || card.id) == claimForm.card_id)
        || insuranceCards.find(card => (card.card_id || card.id) == claimForm.card_id));
      const selectedInv = invoices.find(inv => inv.id == claimForm.invoice_id);

      if (selectedCard && selectedInv) {
        setSelectedInvoice(selectedInv);

        const invoiceAmount = Number(selectedInv.total_amount || selectedInv.amount || 0);
        const deductibleAmount = Number(selectedCard.deductible_amount || 0);
        const coInsurancePercent = Number(
          (overrideCoInsPercent !== "" && overrideCoInsPercent !== null && overrideCoInsPercent !== undefined)
            ? overrideCoInsPercent
            : (selectedCard.co_insurance_percent || 0)
        );

        const afterDeductible = claimForm.is_deductible_applied
          ? Math.max(0, invoiceAmount - deductibleAmount)
          : invoiceAmount;

        const claimAmount = (afterDeductible * coInsurancePercent) / 100;
        const patientAmount = (claimForm.is_deductible_applied ? deductibleAmount : 0) + (afterDeductible - claimAmount);

        setClaimCalculations({
          invoiceAmount,
          deductibleAmount,
          afterDeductible,
          coInsurancePercent,
          claimAmount,
          patientAmount
        });
      }
    }
  }, [claimForm, insuranceCards, activeClaimCards, invoices, overrideCoInsPercent]);

  const submitNewClaim = async (e) => {
    e.preventDefault();
    if (!claimForm.card_id || !claimForm.invoice_id) {
      Swal.fire("Error", "Please select both insurance card and invoice", "error");
      return;
    }

    try {
      setLoading(true);
      const selectedCard = (activeClaimCards.find(card => (card.card_id || card.id) == claimForm.card_id)
        || insuranceCards.find(card => (card.card_id || card.id) == claimForm.card_id));
      const finalCoInsPercent = Number(
        (overrideCoInsPercent !== "" && overrideCoInsPercent !== null && overrideCoInsPercent !== undefined)
          ? overrideCoInsPercent
          : (selectedCard?.co_insurance_percent || 0)
      );
      const payload = {
        patient_id: Number(patientId),
        card_id: Number(claimForm.card_id),
        invoice_id: Number(claimForm.invoice_id),
        is_deductible_applied: claimForm.is_deductible_applied ? 1 : 0,
        co_insurance_percent: finalCoInsPercent
      };

      const response = await axiosInstance.post(`addInsuranceClaim`, payload);

      if (response.data?.success) {
        Swal.fire("Success", "Insurance claim created successfully", "success");
        setShowNewClaimModal(false);
        setClaimForm({
          card_id: "",
          invoice_id: "",
          is_deductible_applied: false,
          status: "Pending"
        });
        setOverrideCoInsPercent("");
        loadClaims();
      } else {
        Swal.fire("Error", response.data?.message || "Failed to create claim", "error");
      }
    } catch (error) {
      console.error("Error creating claim:", error);
      Swal.fire("Error", "Failed to create insurance claim", "error");
    } finally {
      setLoading(false);
    }
  };


  // Recalculate when edit claim form changes
  useEffect(() => {
    if (!editingClaim) return;
    const invoiceAmount = Number(editingClaim.invoice_total || 0);
    const deductibleAmount = Number(editingClaim.deductible_amount || 0);
    const coInsurancePercent = Number(editClaimForm.co_insurance_percent || 0);
    const afterDeductible = editClaimForm.is_deductible_applied
      ? Math.max(0, invoiceAmount - deductibleAmount)
      : invoiceAmount;
    const claimAmount = (afterDeductible * coInsurancePercent) / 100;
    const patientAmount = (editClaimForm.is_deductible_applied ? deductibleAmount : 0) + (afterDeductible - claimAmount);
    setEditClaimCalc({ invoiceAmount, deductibleAmount, afterDeductible, coInsurancePercent, claimAmount, patientAmount });
  }, [editClaimForm, editingClaim]);

  const openViewClaim = (claim) => {
    setViewingClaim(claim);
    setShowViewClaimModal(true);
  };

  const openEditClaim = (claim) => {
    setEditingClaim(claim);
    setEditClaimForm({
      is_deductible_applied: !!claim.is_deductible_applied,
      co_insurance_percent: Number(claim.co_insurance_percent || 0),
      manual_override: false,
      manual_insurance_amount: Number(claim.insurance_amount || 0).toFixed(3),
      manual_patient_amount: Number(claim.patient_amount || 0).toFixed(3)
    });
    setShowEditClaimModal(true);
  };

  const handleDeleteClaim = async (claim) => {
    const result = await Swal.fire({
      title: "Delete Claim",
      text: `Are you sure you want to delete claim ${claim.claim_number}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete"
    });
    if (!result.isConfirmed) return;
    try {
      const resp = await axiosInstance.delete(`deleteInsuranceClaim/${claim.claim_id}`);
      if (resp.data?.success) {
        Swal.fire("Deleted", "Claim deleted successfully", "success");
        loadClaims();
      } else {
        Swal.fire("Error", resp.data?.message || "Failed to delete claim", "error");
      }
    } catch (e) {
      Swal.fire("Error", "Failed to delete claim", "error");
    }
  };

  const submitEditClaim = async (e) => {
    e.preventDefault();
    if (editClaimForm.manual_override) {
      const insAmt = parseFloat(editClaimForm.manual_insurance_amount || 0);
      const patAmt = parseFloat(editClaimForm.manual_patient_amount || 0);
      const invoiceTotal = Number(editingClaim.invoice_total || 0);
      if (insAmt + patAmt > invoiceTotal) {
        Swal.fire("Invalid Amounts", `Insurance + Patient total (${(insAmt + patAmt).toFixed(3)} BHD) cannot exceed the invoice total (${invoiceTotal.toFixed(3)} BHD).`, "error");
        return;
      }
    }
    try {
      setLoading(true);
      const payload = editClaimForm.manual_override
        ? {
            manual_override: true,
            is_deductible_applied: editClaimForm.is_deductible_applied ? 1 : 0,
            insurance_amount: parseFloat(editClaimForm.manual_insurance_amount),
            patient_amount: parseFloat(editClaimForm.manual_patient_amount)
          }
        : {
            is_deductible_applied: editClaimForm.is_deductible_applied ? 1 : 0,
            co_insurance_percent: Number(editClaimForm.co_insurance_percent)
          };
      const resp = await axiosInstance.put(`updateInsuranceClaim/${editingClaim.claim_id}`, payload);
      if (resp.data?.success) {
        Swal.fire("Success", "Claim updated successfully", "success");
        setShowEditClaimModal(false);
        setEditingClaim(null);
        loadClaims();
      } else {
        Swal.fire("Error", resp.data?.message || "Failed to update claim", "error");
      }
    } catch (e) {
      Swal.fire("Error", "Failed to update claim", "error");
    } finally {
      setLoading(false);
    }
  };

  const printSingleClaim = async (claim) => {
    const hospitalSettings = typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("businessSetting"))
      : null;

    const hospitalName = hospitalSettings?.official_name || "Hospital Name";
    const hospitalCR = hospitalSettings?.cr_number || "";
    const hospitalVAT = hospitalSettings?.vat_number || "";
    const hospitalEmail = hospitalSettings?.email || "";
    const hospitalPhone = hospitalSettings?.telephone_1 || "";
    const website = hospitalSettings?.website || "";

    const createdDate = (() => {
      try {
        const d = new Date(claim.created_at);
        return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB");
      } catch { return "-"; }
    })();

    const deductibleAmt = Number(claim.deductible_amount || 0);
    const invoiceTotal = Number(claim.invoice_total || 0);
    const coInsPercent = Number(claim.co_insurance_percent || 0);
    const insuranceAmt = Number(claim.insurance_amount || claim.remaining_ins_amount || 0);
    const patientAmt = Number(claim.patient_amount || 0);
    const isDeductible = !!claim.is_deductible_applied;
    const afterDeductible = isDeductible ? Math.max(invoiceTotal - deductibleAmt, 0) : invoiceTotal;

    // Fetch invoice line items
    let services = [], pharmacy = [], labs = [];
    try {
      const res = await axiosInstance.get(`getInvoiceById/${claim.invoice_id}`);
      if (res.data?.success && res.data?.data) {
        services = res.data.data.services || [];
        pharmacy = res.data.data.pharmacy || [];
        labs = res.data.data.labs || [];
      }
    } catch (_) {}

    // Build all line items
    const allItems = [
      ...services.map(s => ({ name: `${s.serviceCode ? s.serviceCode + ' - ' : ''}${s.serviceName}`, type: 'Service', amount: Number(s.net_amount || s.price || 0), quantity: Number(s.quantity) || 1 })),
      ...pharmacy.map(p => ({ name: p.serviceName, type: 'Pharmacy', amount: Number(p.net_amount || p.price || 0), quantity: Number(p.quantity) || 1 })),
      ...labs.map(l => ({ name: l.serviceName, type: 'Lab', amount: Number(l.net_amount || 0), quantity: Number(l.quantity) || 1 })),
    ];

    const itemRows = allItems.length > 0 ? allItems.map((item, idx) => {
      const insAmt = (item.amount * coInsPercent) / 100;
      const patAmt = item.amount - insAmt;
      return `<tr>
        <td>${idx + 1}</td>
        <td>${item.name}</td>
        <td style="text-align:center; color:#666; font-size:11px">${item.type}</td>
        <td style="text-align:center">${item.quantity}</td>
        <td style="text-align:right">${item.amount.toFixed(3)}</td>
        <td style="text-align:right; color:#318965">${insAmt.toFixed(3)}</td>
        <td style="text-align:right; color:#dc2626">${patAmt.toFixed(3)}</td>
      </tr>`;
    }).join('') : `<tr><td colspan="7" style="text-align:center;color:#888">No line items found</td></tr>`;

    const html = `<!DOCTYPE html>
<html>
<head>
<title>Insurance Claim — ${claim.claim_number}</title>
<style>
  @page { size: A4; margin: 10mm; }
  body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; color: #111; }
  .logo { text-align: center; margin-bottom: 10px; }
  .header { display: flex; justify-content: space-between; margin-bottom: 16px; }
  .header-left p, .header-right p { margin: 2px 0; font-size: 12px; }
  h2.title { text-align: center; font-size: 18px; font-weight: 700; margin: 14px 0 6px; border-bottom: 2px solid #333; padding-bottom: 5px; }
  .section-title { font-size: 13px; font-weight: 700; margin: 16px 0 6px; border-left: 3px solid #318965; padding-left: 8px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin: 10px 0; }
  .info-row { display: flex; gap: 6px; font-size: 12px; }
  .info-label { color: #555; min-width: 130px; }
  .info-value { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th, td { border: 1px solid #ccc; padding: 5px 8px; font-size: 11px; }
  th { background: #f0f4f0; font-weight: 700; text-align: left; }
  .summary-table td { font-size: 12px; padding: 5px 10px; }
  .ins-color { color: #318965; }
  .pat-color { color: #dc2626; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
  .badge-success { background: #d1fae5; color: #065f46; }
  .badge-danger { background: #fee2e2; color: #991b1b; }
  .badge-warning { background: #fef9c3; color: #92400e; }
  .terms { margin-top: 20px; font-size: 11px; color: #555; }
  .terms h5 { font-size: 12px; margin-bottom: 4px; }
  .footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 11px; color: #333; border-top: 1px solid #ddd; padding-top: 4px; background: #fff; }
</style>
</head>
<body>
<div class="logo">
  <img height="65" src="${hospitalLogo || ''}" />
</div>
<div class="header">
  <div class="header-left">
    <p><strong>${hospitalName}</strong></p>
    <p><strong>CR:</strong> ${hospitalCR}</p>
    <p><strong>VAT No:</strong> ${hospitalVAT}</p>
  </div>
  <div class="header-right" style="text-align:right">
    <p><strong>Claim No:</strong> ${claim.claim_number}</p>
    <p><strong>Date:</strong> ${createdDate}</p>
    <p><strong>Status:</strong> <span class="badge ${claim.status === 'accepted' ? 'badge-success' : claim.status === 'rejected' ? 'badge-danger' : 'badge-warning'}">${(claim.status || 'pending').toUpperCase()}</span></p>
  </div>
</div>

<h2 class="title">Insurance Claim Form</h2>

<div class="section-title">Patient &amp; Policy Information</div>
<div class="info-grid">
  <div class="info-row"><span class="info-label">Patient Name:</span><span class="info-value">${claim.patient_name || ""}</span></div>
  <div class="info-row"><span class="info-label">Insurance Company:</span><span class="info-value">${claim.company_name || ""}</span></div>
  <div class="info-row"><span class="info-label">Mobile:</span><span class="info-value">${claim.mobileNumber || ""}</span></div>
  <div class="info-row"><span class="info-label">Policy Number:</span><span class="info-value">${claim.policy_number || ""}</span></div>
  <div class="info-row"><span class="info-label">Civil ID:</span><span class="info-value">${claim.civilIdNumber || ""}</span></div>
  <div class="info-row"><span class="info-label">Invoice No:</span><span class="info-value">${claim.invoice_no || claim.invoiceNumber || claim.invoice_id || ""}</span></div>
</div>

<div class="section-title">Service Details</div>
<table>
  <thead>
    <tr>
      <th>#</th>
      <th>Service / Item</th>
      <th style="text-align:center">Type</th>
      <th style="text-align:center">Qty</th>
      <th style="text-align:right">Amount (BHD)</th>
      <th style="text-align:right">Insurance Covers (BHD)</th>
      <th style="text-align:right">Patient Pays (BHD)</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows}
  </tbody>
</table>

<div class="section-title">Claim Summary</div>
<table class="summary-table" style="width:50%; margin-left:auto">
  <tr><td>Invoice Total</td><td style="text-align:right"><strong>${invoiceTotal.toFixed(3)} BHD</strong></td></tr>
  <tr><td>Deductible${isDeductible ? ' <span style="color:#318965">(Applied)</span>' : ' <span style="color:#888">(Not Applied)</span>'}</td><td style="text-align:right">${deductibleAmt.toFixed(3)} BHD</td></tr>
  <tr><td>After Deductible</td><td style="text-align:right">${afterDeductible.toFixed(3)} BHD</td></tr>
  <tr style="background:#f0f9f4"><td class="ins-color"><strong>Insurance Amount (Covered)</strong></td><td style="text-align:right" class="ins-color"><strong>${insuranceAmt.toFixed(3)} BHD</strong></td></tr>
  <tr style="background:#fff5f5"><td class="pat-color"><strong>Patient Amount (Due)</strong></td><td style="text-align:right" class="pat-color"><strong>${patientAmt.toFixed(3)} BHD</strong></td></tr>
</table>

<div class="terms">
  <h5>Terms &amp; Conditions</h5>
  <ul><li>${termsCondition?.content || "Insurance claims are subject to approval by the respective insurance company. Patient co-payment amounts must be settled at the time of service."}</li></ul>
</div>

<div class="footer">
  ${hospitalName} | Email: ${hospitalEmail} | Phone: ${hospitalPhone}${website ? ' | ' + website : ''}
</div>

<script>
  window.onload = () => { setTimeout(() => window.print(), 300); };
  window.onafterprint = () => window.close();
</script>
</body>
</html>`;

    const printWindow = window.open("", "_blank");
    if (!printWindow) { Swal.fire("Error", "Please allow popups to print", "error"); return; }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handlePrint = async () => {
    try {
      setPrintLoading(true);

      // Get hospital settings from localStorage (same as PatientBilling)
      const hospitalSettings = typeof window !== "undefined"
        ? JSON.parse(localStorage.getItem("businessSetting"))
        : null;

      const hospitalCR = hospitalSettings?.cr_number || "";
      const hospitalVAT = hospitalSettings?.vat_number || "";
      const hospitalEmail = hospitalSettings?.email || "abcd@gmail.com";
      const hospitalPhone = hospitalSettings?.telephone_1 || "+973 35135498";
      const hospitalName = hospitalSettings?.official_name || "Hospital Name";
      const website = hospitalSettings?.website || "";

      const response = await axiosInstance.get(`getClaimsByPatientIdPdf/${patientId}`);
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];

      const dataToPrint = list.length ? list : claims;

      if (!dataToPrint || dataToPrint.length === 0) {
        Swal.fire("No Data", "No claims available to print", "warning");
        return;
      }

      // Calculate totals
      const totalInsuranceAmt = dataToPrint.reduce((sum, c) => sum + (Number(c.insurance_amount) || 0), 0);
      const totalPatientAmt = dataToPrint.reduce((sum, c) => sum + (Number(c.patient_amount) || 0), 0);
      const totalInvoiceAmt = dataToPrint.reduce((sum, c) => sum + (Number(c.invoice_total) || 0), 0);

      const rows = dataToPrint.map((c, idx) => {
        const createdDate = (() => {
          try {
            const d = new Date(c.created_at || c.createdAt || c.date);
            return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB');
          } catch { return '-'; }
        })();

        return `
          <tr>
            <td style="border: 1px solid #000;">${idx + 1}</td>
            <td style="border: 1px solid #000;">${c.claim_number || ''}</td>
            <td style="border: 1px solid #000;">${c.invoice_no || c.invoiceNumber || c.invoice_id || ''}</td>
            <td style="border: 1px solid #000; text-align: right;">${Number(c.invoice_total || 0).toFixed(3)} BHD</td>
            <td style="border: 1px solid #000; text-align: center;">${(String(c.is_deductible_applied || c.is_deductible) === '1' || c.is_deductible_applied === true) ? 'Yes' : 'No'}</td>
            <td style="border: 1px solid #000; text-align: right;">${Number(c.co_insurance_percent || 0)}%</td>
            <td style="border: 1px solid #000; text-align: right; color: #318965;">${Number(c.insurance_amount || 0).toFixed(3)} BHD</td>
            <td style="border: 1px solid #000; text-align: right; color: #dc2626;">${Number(c.patient_amount || 0).toFixed(3)} BHD</td>
            <td style="border: 1px solid #000;">${c.status || ''}</td>
            <td style="border: 1px solid #000;">${createdDate}</td>
          </tr>
        `;
      }).join('');

      const html = `<!DOCTYPE html>
<html>
<head>
<title>Insurance Claims Summary</title>
<style>
  @page {
  size: auto;
  margin: 5mm; /* bottom margin for footer */
}
body {
  font-family: Arial, sans-serif;
  line-height: 1.2;
  margin:30px;
}
 
.logo {
  text-align: center;
}

.top-section {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.billingInvoice h2 {
  font-size: 25px;
  font-weight: 600;
}

.patient-info {
  text-align: left;
  font-size: 14px;
}

.invoice-title {
  margin-top: 30px;
  text-align: center;
}

.billingInvoice p,
.billingInvoice h2 {
  margin-top: 0px;
  margin-bottom: 0px;
}

.paymentLine {
  display: flex;
  justify-content: end;
  margin-top: 50px;
}

.paymentLine p {
  margin-right: 10px;
  padding-right: 10px;
  border-right: 1px solid #000;
}
.paymentLine p:last-child{
border:unset;
}
table th,
table td {
  text-align: left;
  padding: 2px 10px;
  font-size:14px
}
.top-section p,.paymentLine p{
font-size:14px
}

.termsACon li{
font-size:12px;
}
 .termsACon ul{
 padding-left:12px;
 }
 
.footer {
  position: fixed;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: #000;
  bottom:0px;
}
</style>
</head>
<body>
<div class="billingInvoice">
  <div class="logo">
    <img height="80" src="${hospitalLogo || ''}" />
  </div>
  <div class="top-section">
    <div class="hospital-info">
      <p><strong>${hospitalName}</strong></p>
      <p><strong>CR:</strong> ${hospitalCR}</p>
      <p><strong>VAT No.</strong> ${hospitalVAT}</p>
    </div>

    <div class="patient-info">
      <div>
        <strong>Patient Name: </strong> ${dataToPrint[0]?.patient_name || dataToPrint[0]?.patient || ''}
      </div>
      <div>
        <strong>Policy #: </strong> ${dataToPrint[0]?.policy_number || ''}
      </div>
      <div>
        <strong>Mobile: </strong> ${dataToPrint[0]?.mobileNumber || dataToPrint[0]?.mobile || ''}
      </div>
      <div>
        <p><strong>Generated on:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
      </div>
    </div>
  </div>
  
  <div class="invoice-title">
    <h2>Insurance Claims Summary</h2>
  </div>
</div>

<div class="paymentLine">
  <p> <strong>Total Invoice Amount: <span style="color:#318965">${totalInvoiceAmt.toFixed(3)} BHD</span> </strong></p>
  <p> <strong>Total Insurance Amount: <span style="color:#318965">${totalInsuranceAmt.toFixed(3)} BHD</span> </strong></p>
  <p> <strong>Total Patient Amount: <span style="color:#dc2626">${totalPatientAmt.toFixed(3)} BHD</span> </strong></p>
</div>

<table style="width: 100%; border-collapse: collapse;">
  <thead>
    <tr>
      <th style="border: 1px solid #000;">#</th>
      <th style="border: 1px solid #000;">Claim No</th>
      <th style="border: 1px solid #000;">Invoice No</th>
      <th style="border: 1px solid #000; text-align: right;">Invoice Total</th>
      <th style="border: 1px solid #000; text-align: center;">Deductible</th>
      <th style="border: 1px solid #000; text-align: right;">Co-Ins %</th>
      <th style="border: 1px solid #000; text-align: right;">Insurance Amt</th>
      <th style="border: 1px solid #000; text-align: right;">Patient Amt</th>
      <th style="border: 1px solid #000;">Status</th>
      <th style="border: 1px solid #000;">Created</th>
    </tr>
  </thead>
  <tbody>
    ${rows}
  </tbody>
  <tfoot>
    <tr>
      <td colspan="3" style="border: 1px solid #000; text-align: right;"><strong>Total</strong></td>
      <td style="border: 1px solid #000; text-align: right;"><strong>${totalInvoiceAmt.toFixed(3)} BHD</strong></td>
      <td style="border: 1px solid #000;"></td>
      <td style="border: 1px solid #000;"></td>
      <td style="border: 1px solid #000; text-align: right; color: #318965;"><strong>${totalInsuranceAmt.toFixed(3)} BHD</strong></td>
      <td style="border: 1px solid #000; text-align: right; color: #dc2626;"><strong>${totalPatientAmt.toFixed(3)} BHD</strong></td>
      <td colspan="2" style="border: 1px solid #000;"></td>
    </tr>
  </tfoot>
</table>

<div class="termsACon">
  <h5 style="margin-top:20px">Terms & Conditions</h5>
  <div style="margin-top:10px;">
    <ul>
      <li>${termsCondition?.content || "Insurance claims are subject to approval by the respective insurance company. Patient co-payment amounts must be settled at the time of service."}</li>
    </ul>
  </div>
</div>

<div class="footer">
  <div style="font-size: 12px; text-align: center; color: rgb(0, 0, 0);">
    Hospital Name: ${hospitalName} | Email: 
    <span style="border-bottom: 1px solid rgb(44, 100, 223); color: rgb(44, 100, 223);">
      ${hospitalEmail}
    </span> 
    | Phone: ${hospitalPhone} | 
    <span style="border-bottom: 1px solid rgb(44, 100, 223); color: rgb(44, 100, 223);">
      Website: ${website}
    </span>
  </div>
</div>

<script>
  window.onload = () => {
    setTimeout(() => window.print(), 200);
  };
  window.onafterprint = () => window.close();
</script>
</body>
</html>`;

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        Swal.fire('Error', 'Please allow popups to print', 'error');
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

    } catch (error) {
      console.error('Error printing claims:', error);
      Swal.fire('Error', 'Failed to print claims', 'error');
    } finally {
      setPrintLoading(false);
    }
  };


  return (
    <div>
      <div>
        <div className="d-flex align-items-center justify-content-between">
          <h5>Insurance</h5>
          <div>
            <div className="d-flex">
              <button className="bgBtn btn-sm d-flex align-items-center gap-1 me-2" style={{ fontSize: 13, padding: "5px 12px" }} onClick={openNewClaimModal}>
                New Claim
              </button>
              <button
                className="bgBtn btn-sm d-flex align-items-center gap-1 me-2"
                style={{ fontSize: 13, padding: "5px 12px" }}
                onClick={() => setShowAddCardModal(true)}
              >
                Add Card
              </button>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className="row">
          <div className="col-lg-8">
            <div className="borderInsure">
              <div className="serviceHis">
                <div className="d-flex justify-content-between tableHeader  secClaim">
                  <div className="d-flex align-items-center">
                    <div className="ps-3">
                      <div className="tableSearch" style={{ position: 'relative', width: '250px' }}>
                        <input
                          type="text"
                          placeholder="Search claims..."
                          className="form-control"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          style={{ paddingLeft: '35px' }}
                        />
                        <span className="searchIcon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="gray"
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "10px",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                            }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap- align-items-center iconTab">
                    {printLoading ? (
                      <div className="spinner-border spinner-border-sm" role="status" title="Generating PDF...">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <i
                        className="ti ti-printer"
                        style={{ cursor: 'pointer' }}
                        onClick={handlePrint}
                        title="Print Claims"
                      ></i>
                    )}
                    {/* <i className="ti ti-edit "></i> */}
                    {/* <button className="borderBtn me-2">
                        Send
                      </button>
                      <button className="bgBtn">Pay</button> */}
                  </div>
                </div>
              </div>
              <div className="card-body p-0 mt-0">
                <div className="table-responsive mt-0">
                  {claimsLoading ? (
                    <div className="text-center p-3">Loading claims...</div>
                  ) : currentClaims.length === 0 ? (
                    <div className="text-center p-3 text-muted">No claims found</div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Claim No</th>
                          <th>Invoice No</th>
                          <th>Invoice Total</th>
                          <th>Deductible</th>
                          <th>Co-Ins %</th>

                          <th>Remaining Insurance Amt</th>
                          <th>Total Insurance Amt</th>
                          <th>Patient Amt</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentClaims.map((c, idx) => {
                          const serial = (currentClaimPage - 1) * claimsPerPage + idx + 1;
                          return (
                            <tr key={c.claim_id || idx}>
                              <td>{serial}</td>
                              <td>{c.claim_number}</td>
                              <td>{c.invoice_no || c.invoiceNumber || c.invoice_id}</td>
                              <td>{Number(c.invoice_total || 0).toFixed(3)} BHD</td>
                              <td>{c.deductible_amount} BHD</td>
                              <td>{Number(c.co_insurance_percent || 0)}%</td>
                              <td className="text-success">{Number(c.remaining_ins_amount || 0).toFixed(3)} BHD</td>
                              <td className="text-success">{Number(c.insurance_amount || 0).toFixed(3)} BHD</td>
                              <td className="text-warning">{Number(c.patient_amount || 0).toFixed(3)} BHD</td>
                              <td>
                                {(c.status || 'pending').toLowerCase() === 'accepted' ? (
                                  <span className="badge bg-success" style={{ fontSize: '0.8em' }}>Accepted</span>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.8em' }}>Pending</span>
                                    <button
                                      className="btn btn-success btn-sm py-0 px-2"
                                      style={{ fontSize: '0.78em' }}
                                      onClick={() => updateClaimStatus(c.claim_id || c.id, 'accepted')}
                                    >
                                      Accept
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td>{formatDateOnly(c.created_at)}</td>
                              <td>
                                <div className="d-flex gap-1 align-items-center">
                                  <button
                                    className="avtar avtar-xs btn-link-secondary viewIcon"
                                    title="View"
                                    onClick={() => openViewClaim(c)}
                                  >
                                    <i className="ti ti-eye f-20" />
                                  </button>
                                  <button
                                    className="avtar avtar-xs btn-link-secondary editIcon"
                                    title="Edit"
                                    onClick={() => openEditClaim(c)}
                                  >
                                    <i className="ti ti-edit f-20" />
                                  </button>
                                  <button
                                    className="avtar avtar-xs btn-link-secondary deleteIcon"
                                    title="Delete"
                                    onClick={() => handleDeleteClaim(c)}
                                  >
                                    <i className="ti ti-trash f-20" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {totalClaimPages > 1 && (
                  <nav className="tablepagination d-flex justify-content-end mt-3 mb-2">
                    <ul className="pagination mb-0">
                      <li
                        className={`page-item ${currentClaimPage === 1 ? "disabled" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            currentClaimPage > 1 &&
                            setCurrentClaimPage(currentClaimPage - 1)
                          }
                        >
                          &laquo;
                        </button>
                      </li>

                      <li className="pageNmbr">{`${currentClaimPage} of ${totalClaimPages}`}</li>

                      <li
                        className={`page-item ${currentClaimPage === totalClaimPages
                          ? "disabled"
                          : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            currentClaimPage < totalClaimPages &&
                            setCurrentClaimPage(currentClaimPage + 1)
                          }
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}

                <div className="d-flex justify-content-end align-items-center px-3 py-2">
                  <div>
                    <span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : insuranceCards.length === 0 ? (
              <div className="text-center p-4">
                <p className="text-muted">No insurance cards found</p>
              </div>
            ) : (
              insuranceCards.map((card, idx) => {
                const company = insuranceCompanies.find(comp => comp.company_id == card.company_id);
                const companyName = company ? company.company_name : 'Unknown Company';

                const currentDate = new Date();
                currentDate.setHours(0, 0, 0, 0);
                const expiryDate = new Date(card.expiry_date);
                expiryDate.setHours(0, 0, 0, 0);
                const isExpired = expiryDate < currentDate;
                const status = isExpired ? "Expired" : "Active";

                const coInsurance = parseFloat(card.co_insurance_percent) || 0;
                const coPatient = 100 - coInsurance;

                return (
                  <div
                    className={`insureCard mb-3`}
                    key={card.card_id || card.id || idx}
                    style={{
                      backgroundColor:
                        status === "Active"
                          ? "#fef5e6"
                          : "#fff7f5",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center tableHeader cardInsureAlign">
                      <div>
                        <h5>Insurance Card {idx + 1}</h5>
                      </div>
                      <div className="d-flex align-items-center gap-2">

                        <p
                          className={`ms-2 badge bg-${status === "Active"
                            ? "success"
                            : "danger"
                            }`}
                        >
                          {status === "Active" ? "Status Active" : "Status Expired"}
                        </p>
                        <button
                          className="bgBtn btn-sm d-flex align-items-center gap-1"
                          style={{ fontSize: 13, padding: "5px 12px" }}
                          onClick={() => openEditModal(card)}
                          title="Edit Card"
                        >
                          <i className="ti ti-edit"></i>
                        </button>
                        <button
                          className="bgBtn btn-sm d-flex align-items-center gap-1"
                          style={{ fontSize: 13, padding: "5px 12px", backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                          onClick={() => handleDeleteCard(card)}
                          title="Delete Card"
                        >
                          <i className="ti ti-trash" style={{ color: "#fff" }}></i>
                        </button>
                      </div>
                    </div>
                    <div className="p20">
                      <p className="mb-1">
                        <strong>Company Name:</strong> {companyName}
                      </p>
                      <p className="mb-1">
                        <strong>Policy Number:</strong> {card.policy_number}
                      </p>
                      <p className="mb-1">
                        <strong>Issue Date:</strong> {formatDateOnly(card.issue_date)}
                      </p>
                      <p className="mb-1">
                        <strong>Expiry Date:</strong> {formatDateOnly(card.expiry_date)}
                      </p>
                      <p className="mb-1">
                        <strong>Deductible:</strong> {card.deductible_amount} BHD
                      </p>
                      <p className="mb-1">
                        <strong>Co-Patient:</strong> {coPatient}%
                      </p>
                      <p className="mb-1">
                        <strong>Co-Insurance:</strong> {coInsurance}%
                      </p>
                      <p className="mb-0">
                        <strong>Scan Copy:</strong>{" "}
                        {card.scanned_copy ? (
                          <a href={`${baseurImage}${card.scanned_copy}`} target="_blank" rel="noopener noreferrer">
                            <i className="bi bi-paperclip"></i> View
                          </a>
                        ) : (
                          <i className="bi bi-paperclip text-muted"></i>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <AddInsuranceCard
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        patientId={patientId}
        onSuccess={handleCardAdded}
      />

      {showNewClaimModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Insurance Claim</h5>

              </div>
              <form onSubmit={submitNewClaim}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-12">
                      <label className="form-label">Insurance Card</label>
                      {activeCardsLoading ? (
                        <div className="form-text">Loading cards...</div>
                      ) : (
                        <select
                          name="card_id"
                          className="form-select"
                          value={claimForm.card_id}
                          onChange={handleClaimFormChange}
                          required
                        >
                          <option value="">Select Insurance Card</option>
                          {activeClaimCards.map((card) => {
                            const company = insuranceCompanies.find(comp => comp.company_id == card.company_id);
                            const companyName = company ? company.company_name : (card.company_name || 'Unknown Company');
                            return (
                              <option key={card.card_id || card.id} value={card.card_id || card.id}>
                                {companyName} - {card.policy_number}
                              </option>
                            );
                          })}
                        </select>
                      )}

                      {claimForm.card_id && (() => {
                        const c = (activeClaimCards.find(x => (x.card_id || x.id) == claimForm.card_id)
                          || insuranceCards.find(x => (x.card_id || x.id) == claimForm.card_id));
                        if (!c) return null;
                        const company = insuranceCompanies.find(comp => comp.company_id == c.company_id);
                        const companyName = company ? company.company_name : (c.company_name || 'Unknown Company');
                        const currentDate = new Date();
                        currentDate.setHours(0, 0, 0, 0);
                        const expiryDate = new Date(c.expiry_date);
                        expiryDate.setHours(0, 0, 0, 0);
                        const isExpired = expiryDate < currentDate;
                        const coInsurance = (overrideCoInsPercent !== "" && overrideCoInsPercent !== null && overrideCoInsPercent !== undefined)
                          ? Math.max(0, Math.min(100, parseFloat(overrideCoInsPercent) || 0))
                          : (parseFloat(c.co_insurance_percent) || 0);
                        const coPatient = Math.max(0, 100 - coInsurance);
                        return (
                          <div className="card bg-light mt-2">
                            <div className="card-body py-2">
                              <div className="row g-2 align-items-center">
                                <div className="col-md-4">
                                  <div className="small text-muted">Company</div>
                                  <div className="fw-semibold">{companyName}</div>
                                </div>
                                <div className="col-md-4">
                                  <div className="small text-muted">Policy #</div>
                                  <div className="fw-semibold">{c.policy_number}</div>
                                </div>
                                <div className="col-md-4 text-md-end">
                                  <span className={`badge ${isExpired ? 'bg-danger' : 'bg-success'}`}>{isExpired ? 'Expired' : 'Active'}</span>
                                </div>
                                <div className="col-md-3">
                                  <div className="small text-muted">Issue</div>
                                  <div>{formatDateOnly(c.issue_date)}</div>
                                </div>
                                <div className="col-md-3">
                                  <div className="small text-muted">Expiry</div>
                                  <div>{formatDateOnly(c.expiry_date)}</div>
                                </div>
                                <div className="col-md-3">
                                  <div className="small text-muted">Deductible</div>
                                  <div>{Number(c.deductible_amount || 0).toFixed(3)} BHD</div>
                                </div>
                                <div className="col-md-3">
                                  <div className="small text-muted">Co-Ins / Co-Pat</div>
                                  <div className="d-flex align-items-center gap-2">
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={overrideCoInsPercent === "" ? (c.co_insurance_percent ?? 0) : overrideCoInsPercent}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "") { setOverrideCoInsPercent(""); return; }
                                        const num = Math.max(0, Math.min(100, Number(val)));
                                        setOverrideCoInsPercent(num);
                                      }}
                                      min="0"
                                      max="100"
                                      step="0.001"
                                      style={{ width: '90px' }}
                                    />
                                    <span>% / {coPatient}%</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="col-12">
                      <label className="form-label">Invoice</label>
                      <select
                        name="invoice_id"
                        className="form-select"
                        value={claimForm.invoice_id}
                        onChange={handleClaimFormChange}
                        required
                      >
                        <option value="">Select Invoice</option>
                        {invoices.map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>
                            {invoice.invoice_no || invoice.invoiceNo || invoice.id} - {Number(invoice.total_amount || invoice.amount || 0).toFixed(3)} BHD
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12">
                      <div>
                        <label className="form-check-label d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            name="is_deductible_applied"
                            className="form-check-input"
                            checked={claimForm.is_deductible_applied}
                            onChange={handleClaimFormChange}
                          />
                          Apply Deductible
                        </label>
                      </div>
                    </div>


                    {claimForm.card_id && claimForm.invoice_id && (
                      <div className="col-12">
                        <div className="card bg-light p-3">
                          <h6>Claim Calculation</h6>
                          <div className="row">
                            <div className="col-md-6">
                              <p className="mb-1"><strong>Invoice Amount:</strong> {claimCalculations.invoiceAmount.toFixed(3)} BHD</p>
                              <p className="mb-1"><strong>Deductible Amount:</strong> {claimCalculations.deductibleAmount.toFixed(3)} BHD</p>
                              <p className="mb-1"><strong>After Deductible:</strong> {claimCalculations.afterDeductible.toFixed(3)} BHD</p>
                            </div>
                            <div className="col-md-6">
                              <p className="mb-1"><strong>Co-Insurance:</strong> {claimCalculations.coInsurancePercent}%</p>
                              <p className="mb-1 text-success"><strong>Claim Amount:</strong> {claimCalculations.claimAmount.toFixed(3)} BHD</p>
                              <p className="mb-1 text-warning"><strong>Patient Amount:</strong> {claimCalculations.patientAmount.toFixed(3)} BHD</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancelText"
                    onClick={() => setShowNewClaimModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bgBtn btn-sm d-flex align-items-center gap-1"
                    style={{ fontSize: 13, padding: "5px 12px" }}
                    disabled={loading}
                  >
                    {loading ? "Creating..." : "Create Claim"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Insurance Card</h5>

              </div>
              <form onSubmit={submitEdit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <label className="form-label">Policy Number</label>
                      <input
                        type="text"
                        name="policy_number"
                        className="form-control"
                        value={editForm.policy_number}
                        onChange={handleEditChange}
                        placeholder="Enter policy number"
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Insurance Company</label>
                      <select
                        name="company_id"
                        className="form-select"
                        value={editForm.company_id}
                        onChange={handleEditChange}
                        required
                      >
                        <option value="">Select Insurance Company</option>
                        {insuranceCompanies.map((company) => (
                          <option key={company.company_id} value={company.company_id}>
                            {company.company_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Issue Date</label>
                      <input
                        type="date"
                        name="issue_date"
                        className="form-control"
                        value={editForm.issue_date}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        name="expiry_date"
                        className="form-control"
                        value={editForm.expiry_date}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Co-Insurance (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editForm.co_insurance_percent !== "" && editForm.co_insurance_percent !== null && editForm.co_insurance_percent !== undefined ? 100 - parseFloat(editForm.co_insurance_percent || 0) : ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "") {
                            setEditForm(prev => ({ ...prev, co_insurance_percent: "" }));
                          } else {
                            const coIns = 100 - parseFloat(val);
                            setEditForm(prev => ({ ...prev, co_insurance_percent: isNaN(coIns) ? "" : String(Math.max(0, Math.min(100, coIns))) }));
                          }
                        }}
                        min="0"
                        max="100"
                        step="0.001"
                        placeholder="Enter patient %"
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Company Share (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editForm.co_insurance_percent}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                      <small className="text-muted">Auto-calculated</small>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Deductible Amount (BHD)</label>
                      <input
                        type="number"
                        name="deductible_amount"
                        className="form-control"
                        value={editForm.deductible_amount}
                        onChange={handleEditChange}
                        min="0"
                        step="0.001"
                        placeholder="e.g., 300"
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Scanned Copy</label>
                      <input
                        type="file"
                        name="scanned_copy"
                        className="form-control"
                        onChange={handleEditChange}
                        accept="image/*,.pdf"
                      />
                      <div className="form-text">
                        Upload a new scanned copy (optional). Leave empty to keep current file.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancelText"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: "5px 12px" }}>
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* Backdrop click disabled to avoid accidental modal close while editing */}
        </div>
      )}

      {/* VIEW CLAIM MODAL */}
      {showViewClaimModal && viewingClaim && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Claim Details — {viewingClaim.claim_number}</h5>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="small text-muted">Claim Number</div>
                    <div className="fw-semibold">{viewingClaim.claim_number}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Insurance Company</div>
                    <div className="fw-semibold">{viewingClaim.company_name || "—"}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Policy Number</div>
                    <div className="fw-semibold">{viewingClaim.policy_number || "—"}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Invoice No</div>
                    <div className="fw-semibold">{viewingClaim.invoice_no || viewingClaim.invoiceNumber || viewingClaim.invoice_id}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">Invoice Total</div>
                    <div>{Number(viewingClaim.invoice_total || 0).toFixed(3)} BHD</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">Deductible</div>
                    <div>{Number(viewingClaim.deductible_amount || 0).toFixed(3)} BHD {viewingClaim.is_deductible_applied ? <span className="badge bg-success">Applied</span> : <span className="badge bg-secondary">Not Applied</span>}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">Co-Insurance %</div>
                    <div>{Number(viewingClaim.co_insurance_percent || 0)}%</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">Insurance Amount</div>
                    <div className="text-success fw-semibold">{Number(viewingClaim.insurance_amount || viewingClaim.remaining_ins_amount || 0).toFixed(3)} BHD</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">Patient Amount</div>
                    <div className="text-warning fw-semibold">{Number(viewingClaim.patient_amount || 0).toFixed(3)} BHD</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">Status</div>
                    <div><span className={`badge ${viewingClaim.status === 'accepted' ? 'bg-success' : viewingClaim.status === 'rejected' ? 'bg-danger' : 'bg-warning text-dark'}`}>{viewingClaim.status}</span></div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">Created</div>
                    <div>{formatDateOnly(viewingClaim.created_at)}</div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="bgBtn btn-sm d-flex align-items-center gap-1"
                  style={{ fontSize: 13, padding: "5px 12px" }}
                  onClick={() => printSingleClaim(viewingClaim)}
                >
                  <i className="ti ti-printer f-16" />
                  Print
                </button>
                <button className="cancelText" onClick={() => setShowViewClaimModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT CLAIM MODAL */}
      {showEditClaimModal && editingClaim && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Claim — {editingClaim.claim_number}</h5>
              </div>
              <form onSubmit={submitEditClaim}>
                <div className="modal-body">
                  <div className="row g-3">
                    {/* Mode toggle */}
                    <div className="col-12 d-flex align-items-center gap-3 pb-1 border-bottom">
                      <span className="fw-semibold" style={{ fontSize: 13 }}>Mode:</span>
                      <div className="form-check form-check-inline mb-0">
                        <input className="form-check-input" type="radio" id="mode-auto" checked={!editClaimForm.manual_override}
                          onChange={() => setEditClaimForm(prev => ({ ...prev, manual_override: false }))} />
                        <label className="form-check-label" htmlFor="mode-auto">Auto-Calculate</label>
                      </div>
                      <div className="form-check form-check-inline mb-0">
                        <input className="form-check-input" type="radio" id="mode-manual" checked={!!editClaimForm.manual_override}
                          onChange={() => setEditClaimForm(prev => ({ ...prev, manual_override: true }))} />
                        <label className="form-check-label" htmlFor="mode-manual">Manual Override</label>
                      </div>
                    </div>

                    {!editClaimForm.manual_override ? (
                      <>
                        <div className="col-12 col-md-6">
                          <label className="form-label">Co-Insurance %</label>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            max="100"
                            step="0.001"
                            value={editClaimForm.co_insurance_percent}
                            onChange={(e) => setEditClaimForm(prev => ({ ...prev, co_insurance_percent: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="col-12 col-md-6 d-flex align-items-end">
                          <label className="form-check-label d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={editClaimForm.is_deductible_applied}
                              onChange={(e) => setEditClaimForm(prev => ({ ...prev, is_deductible_applied: e.target.checked }))}
                            />
                            Apply Deductible ({Number(editingClaim.deductible_amount || 0).toFixed(3)} BHD)
                          </label>
                        </div>
                        <div className="col-12">
                          <div className="card bg-light p-3">
                            <h6>Recalculated Amounts</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <p className="mb-1"><strong>Invoice Amount:</strong> {editClaimCalc.invoiceAmount.toFixed(3)} BHD</p>
                                <p className="mb-1"><strong>Deductible:</strong> {editClaimCalc.deductibleAmount.toFixed(3)} BHD</p>
                                <p className="mb-1"><strong>After Deductible:</strong> {editClaimCalc.afterDeductible.toFixed(3)} BHD</p>
                              </div>
                              <div className="col-md-6">
                                <p className="mb-1 text-success"><strong>Insurance Amount:</strong> {editClaimCalc.claimAmount.toFixed(3)} BHD</p>
                                <p className="mb-1 text-warning"><strong>Patient Amount:</strong> {editClaimCalc.patientAmount.toFixed(3)} BHD</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-12">
                          <div className="alert alert-info py-2 mb-0" style={{ fontSize: 13 }}>
                            <i className="ti ti-info-circle me-1" />
                            Enter the exact amounts to store. Invoice total: <strong>{Number(editingClaim.invoice_total || 0).toFixed(3)} BHD</strong>
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label text-success fw-semibold">Insurance Pays (BHD)</label>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            step="0.001"
                            value={editClaimForm.manual_insurance_amount}
                            onChange={(e) => setEditClaimForm(prev => ({ ...prev, manual_insurance_amount: e.target.value }))}
                            placeholder="0.000"
                            required
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label text-warning fw-semibold">Patient Pays (BHD)</label>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            step="0.001"
                            value={editClaimForm.manual_patient_amount}
                            onChange={(e) => setEditClaimForm(prev => ({ ...prev, manual_patient_amount: e.target.value }))}
                            placeholder="0.000"
                            required
                          />
                        </div>
                        <div className="col-12">
                          {(() => {
                            const total = parseFloat(editClaimForm.manual_insurance_amount || 0) + parseFloat(editClaimForm.manual_patient_amount || 0);
                            const invoiceTotal = Number(editingClaim.invoice_total || 0);
                            const exceeded = total > invoiceTotal;
                            return (
                              <div className={`card p-2 d-flex flex-row align-items-center gap-3 ${exceeded ? 'bg-danger bg-opacity-10 border-danger' : 'bg-light'}`}>
                                <span style={{ fontSize: 13 }}>
                                  Total: <strong>{total.toFixed(3)} BHD</strong>
                                  {' / '}
                                  Invoice: <strong>{invoiceTotal.toFixed(3)} BHD</strong>
                                </span>
                                {exceeded && (
                                  <span className="text-danger fw-semibold" style={{ fontSize: 13 }}>
                                    <i className="ti ti-alert-triangle me-1" />
                                    Exceeds invoice total by {(total - invoiceTotal).toFixed(3)} BHD
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="cancelText" onClick={() => setShowEditClaimModal(false)}>Cancel</button>
                  <button type="submit" className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: "5px 12px" }} disabled={loading}>
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Hidden (off-screen) PDF Target: must be rendered (not display:none) */}
      <div ref={targetRef} style={{ position: 'absolute', left: '-9999px', top: 0, width: '1000px', background: '#fff' }}>
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>Insurance Claims (Patient wise)</h2>
            <div style={{ color: '#7f8c8d', fontSize: '12px' }}>Generated on: {new Date().toLocaleDateString('en-GB')}</div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>#</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Claim No</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Invoice No</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Invoice Total</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Deductible</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Co-Ins %</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Insurance Amt</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Patient Amt</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Status</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', background: '#f4f6f8' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c, idx) => (
                <tr key={c.claim_id || idx}>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{c.claim_number}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{c.invoice_no || c.invoiceNumber || c.invoice_id}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{Number(c.invoice_total || 0).toFixed(3)} BHD</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{c.is_deductible_applied ? 'Yes' : 'No'}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{Number(c.co_insurance_percent || 0)}%</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{Number(c.insurance_amount || 0).toFixed(3)} BHD</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{Number(c.patient_amount || 0).toFixed(3)} BHD</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{c.status}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{formatDateOnly(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '10px', textAlign: 'right', fontSize: '12px' }}>
            <strong>Total Claims: {claims?.length || 0}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}









